export const TOUCH_MODAL_MAX_WIDTH = 1367

export interface ModalViewportState {
    isHydrated: boolean
    isMobile?: boolean
    isTablet?: boolean
    isTouchDevice: boolean
    viewportWidth?: number
}

export type EditorRenderMode = 'modal' | 'page'
export type ModalPresentation = 'fullscreen' | 'popup' | 'page'
export type ModalApp = 'partner' | 'customer'

const TABLET_BROWSER_MAX_WIDTH = 1050
const INTERCEPTING_ROUTE_MARKER = /^(?:\(\.\)|\(\.\.\)|\(\.\.\.\))+/

export function shouldUseFullscreenModal({
    isHydrated,
    isMobile,
    isTablet,
    isTouchDevice,
    viewportWidth = 0,
}: ModalViewportState) {
    if (!isHydrated) return false
    if (isMobile || isTablet) return true
    if (viewportWidth > 0 && viewportWidth < TABLET_BROWSER_MAX_WIDTH) return true
    return isTouchDevice && viewportWidth < TOUCH_MODAL_MAX_WIDTH
}

export function resolveModalPresentation(renderMode: EditorRenderMode, isFullscreenViewport: boolean): ModalPresentation {
    if (renderMode === 'page') return 'page'
    return isFullscreenViewport ? 'fullscreen' : 'popup'
}

const normalizePath = (value: string) => {
    const path = value.split(/[?#]/, 1)[0].replace(/\/+$/, '')
    return path || '/'
}

export function resolveDefaultModalBackground(app: ModalApp, targetPath: string, notificationSettingsPath?: string): string {
    const segments = normalizePath(targetPath).split('/').filter(Boolean)
    const [section, subsection, area, leaf] = segments

    if (section === 'courses') return '/courses'

    if (app === 'customer') {
        if (section === 'payments' && ['contracts', 'history', 'passes'].includes(subsection)) {
            return `/payments/${subsection}`
        }
        if (section === 'calendar') {
            return subsection === 'events' ? '/calendar/events' : '/calendar'
        }
        if (['my-classes', 'profile'].includes(section)) return `/${section}`
        return '/home'
    }

    if (section === 'calendar') {
        if (subsection === 'private-bookings') return '/calendar/studio-plan'
        return subsection === 'events' ? '/calendar/events' : '/calendar/group-classes'
    }
    if (section === 'signups') return '/clients'
    if (section === 'clients') {
        if (subsection === 'groups') return '/clients/groups'
        // A record opened from a client (pass, payment, class, message) sits on that client's tab.
        const tab = area && ({classes: 'classes', communication: 'communication', passes: 'passes', payments: 'payments'} as Record<string, string>)[area]
        return tab ? `/clients/${subsection}?tab=${tab}` : '/clients'
    }
    if (section === 'payments' && subsection) {
        return subsection === 'passes' ? '/payments/history' : `/payments/${subsection}`
    }
    if (section === 'templates' && subsection) return `/templates/${subsection}`
    if (section === 'more') {
        if (subsection === 'settings' && area === 'integrations') return '/more/settings/integrations'
        if (subsection === 'modules' || subsection === 'settings') return `/more/${subsection}`
        if (notificationSettingsPath && ((subsection === 'communications' && area === 'automations')
            || (subsection === 'automation' && area === 'communications'))) {
            return notificationSettingsPath
        }
        if (subsection === 'automation' && area) return `/more/automation/${area}`
        if (subsection === 'communications' && area) {
            return ['email', 'sms'].includes(area) && leaf === 'templates'
                ? `/more/communications/${area}/templates`
                : `/more/communications/${area}`
        }
        return '/more'
    }
    return '/dashboard'
}

const modalPath = (segments: string[]) => normalizePath(`/${segments
    .flatMap(segment => segment.split('/'))
    .map(segment => segment.replace(INTERCEPTING_ROUTE_MARKER, ''))
    .filter(segment => segment && !segment.startsWith('__') && !(segment.startsWith('(') && segment.endsWith(')')))
    .join('/')}`)

export function resolveRoutedModal(input: {
    currentPath: string
    modalSegments: string[]
    pageSegments?: string[]
    hasModalContent: boolean
    isDirectModalPage?: boolean
    isHydrated: boolean
    isFullscreenViewport: boolean
    recoveryFailed?: boolean
}) {
    const hasPlaceholder = input.modalSegments.some(segment => segment.startsWith('__'))
    // Only an intercepting route renders a modal; a plain slot page that empties the slot never does.
    const isIntercepted = input.modalSegments.some(segment => INTERCEPTING_ROUTE_MARKER.test(segment))
    const preferDirectPage = Boolean(input.isDirectModalPage)
        && (!input.pageSegments || modalPath(input.pageSegments) === normalizePath(input.currentPath))
        && input.isHydrated
        && input.isFullscreenViewport
    const hasRoutedModal = input.hasModalContent
        && isIntercepted
        && !hasPlaceholder
        && !preferDirectPage
        && modalPath(input.modalSegments) === normalizePath(input.currentPath)
    const suppressDirectPage = Boolean(input.isDirectModalPage)
        && !hasRoutedModal
        && !input.recoveryFailed
        && (!input.isHydrated || !input.isFullscreenViewport)

    return {
        hasRoutedModal,
        showModalOnly: suppressDirectPage || (hasRoutedModal && (!input.isHydrated || input.isFullscreenViewport)),
    }
}
